// src/componentes/espera/NubeParticulas.jsx — la espera cuando se lee un equipo
//
// Una nube de partículas con forma de esfera que gira despacio y respira.
// La usan la OLT (al entrar y en su pestaña de configuración) y el switch:
// cada pantalla le pasa SUS lecturas, su umbral de "esto ya tardó" y su
// aviso, y así las tres esperas se ven iguales.
//
// Cómo está hecha: cada partícula tiene una dirección, una distancia al
// centro —más densa hacia la superficie, para que se lea la esfera— y su
// propio vaivén. La nube entera gira sobre el eje vertical, un poco
// inclinada, y se proyecta con perspectiva: las de delante salen más
// grandes y más opacas. Va en canvas porque son casi mil puntos por
// cuadro. Con `prefers-reduced-motion` se dibuja un cuadro y se queda
// quieta.
//
// El reloj está porque es lo único que se puede afirmar con certeza: el
// equipo no informa de su avance, así que un contador es toda la
// diferencia entre "está trabajando" y "se colgó".

import React, { useEffect, useRef } from "react";
import { useTranscurrido } from "../../utils/useTranscurrido";
import "./NubeParticulas.css";

const LADO = 280;        // el lienzo, en píxeles de CSS
const PARTICULAS = 950;
const FOCO = 420;        // distancia de la cámara: menos es más perspectiva
const INCLINACION = 0.38; // radianes

/** Las partículas se reparten una vez; lo que cambia en cada cuadro es el
    giro, el respiro y el vaivén de cada una. */
function repartir() {
  return Array.from({ length: PARTICULAS }, () => {
    const u = Math.random() * 2 - 1;
    const th = Math.random() * Math.PI * 2;
    const s = Math.sqrt(1 - u * u);
    return {
      dx: s * Math.cos(th),
      dy: u,
      dz: s * Math.sin(th),
      rho: 0.5 + 0.5 * Math.pow(Math.random(), 0.45),
      fase: Math.random() * Math.PI * 2,
      vel: 0.6 + Math.random() * 1.2,
    };
  });
}

function dibujar(ctx, puntos, t, tono) {
  const c = LADO / 2;
  const R = LADO * 0.37 * (1 + 0.05 * Math.sin(t * 1.5));
  const giro = t * 0.45;
  const cg = Math.cos(giro), sg = Math.sin(giro);
  const ci = Math.cos(INCLINACION), si = Math.sin(INCLINACION);

  ctx.clearRect(0, 0, LADO, LADO);
  for (const p of puntos) {
    const r = R * (p.rho + 0.08 * Math.sin(t * p.vel + p.fase));
    const x = p.dx * r, y = p.dy * r, z = p.dz * r;
    const x1 = x * cg + z * sg;
    const z1 = -x * sg + z * cg;
    const y2 = y * ci - z1 * si;
    const z2 = y * si + z1 * ci;
    const k = FOCO / (FOCO + z2);
    const cerca = Math.max(0, Math.min(1, 0.5 - z2 / (2 * R)));

    ctx.beginPath();
    ctx.arc(c + x1 * k, c + y2 * k, 0.6 + 1.6 * cerca, 0, Math.PI * 2);
    ctx.fillStyle = `hsl(${tono} / ${(0.18 + 0.72 * cerca).toFixed(3)})`;
    ctx.fill();
  }
}

function Nube() {
  const lienzo = useRef(null);

  useEffect(() => {
    const cv = lienzo.current;
    const ctx = cv && cv.getContext && cv.getContext("2d");
    if (!ctx) return undefined; // sin canvas (pruebas, navegadores viejos) no se dibuja nada

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    cv.width = LADO * dpr;
    cv.height = LADO * dpr;
    ctx.scale(dpr, dpr);

    // El azul sale de los tokens, para que siga al tema de la app.
    const tono = getComputedStyle(document.documentElement).getPropertyValue("--primario").trim() || "221 83% 53%";
    const puntos = repartir();

    const quieta = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (quieta) {
      dibujar(ctx, puntos, 0, tono);
      return undefined;
    }

    let pedido;
    const inicio = performance.now();
    const paso = (ahora) => {
      dibujar(ctx, puntos, (ahora - inicio) / 1000, tono);
      pedido = requestAnimationFrame(paso);
    };
    pedido = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(pedido);
  }, []);

  return <canvas ref={lienzo} className="nube__lienzo" style={{ width: LADO, height: LADO }} />;
}

/**
 * @param titulo   qué se está leyendo.
 * @param nota     la frase de debajo: qué es lo que tarda.
 * @param lecturas [{ icono, rotulo }] lo que se le pregunta al equipo.
 * @param umbral   segundos a partir de los cuales ya no es la lentitud normal.
 * @param aviso    lo que se dice al pasar el umbral.
 */
export default function NubeParticulas({ titulo, nota, lecturas = [], umbral = 25, aviso }) {
  const seg = useTranscurrido(true);
  const mm = String(Math.floor(seg / 60)).padStart(2, "0");
  const ss = String(seg % 60).padStart(2, "0");

  return (
    <div className="nube" role="status" aria-live="polite">
      <div className="nube__marca" aria-hidden="true">
        <span className="nube__marca-texto">WIFIMEX</span>
        <span className="nube__marca-pista"><i /></span>
      </div>

      <div className="nube__escena" aria-hidden="true"><Nube /></div>

      <div className="nube__pie">
        <h3>{titulo}<span className="nube__reloj">{mm}:{ss}</span></h3>
        <p>
          {nota}
          {seg > umbral && aviso && <> <b>Lleva más de {umbral} segundos</b>; {aviso}.</>}
        </p>
        {lecturas.length > 0 && (
          <ul className="nube__lista">
            {lecturas.map((l) => <li key={l.rotulo}><l.icono size={13} strokeWidth={2} />{l.rotulo}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}
